import { Component, OnInit, computed, effect, inject, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import { ConfirmationService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { BreadcrumbService } from '@card-software/library';
import {
  CashFunding,
  CashFundingForm,
  CashFundingInsert,
  CashFundingStatus,
  CashFundingUpdate,
  S3Object,
} from '../../../models/cash-funding/cash-funding';
import { CashRequest } from '../../../models/cash-request/cash-request';
import { BankAccount, BankAccountStatus, Currency } from '../../../models/bank-account/bank-account';
import { CashFundingProxyService } from '../../../proxies/cash-funding.proxy.service';
import { CashRequestProxyService } from '../../../proxies/cash-request.proxy.service';
import { BankAccountProxyService } from '../../../proxies/bank-account.proxy.service';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { currencyLabel as toCurrencyLabel } from '../../../utilities/currency.util';

interface SelectOption {
  label: string;
  value: string;
}

/**
 * Cash Funding — single screen for new / edit / view (the old separate "receive" screen is folded in here).
 *
 * - **New** (no `id`): pick an Approved-and-unfunded Cash Request; its breakdown, company and currency load,
 *   the funder is derived from the request's company, and the amount defaults to the request total.
 * - **Edit** (`id`, still Draft): destination bank, amount and bank reference are editable and can be saved,
 *   attachments added, and the funding posted.
 * - **View** (`id`, Posted/Cancelled): read-only.
 *
 * The summary always shows the under/over-request difference against the **live funding amount** (the actual
 * amount being received), so editing the amount updates it.
 */
@Component({
  selector: 'app-cash-funding-details',
  templateUrl: './cash-funding-details.component.html',
  styleUrl: './cash-funding-details.component.scss',
})
export class CashFundingDetailsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly formBuilder = inject(FormBuilder);
  private readonly service = inject(CashFundingProxyService);
  private readonly cashRequestService = inject(CashRequestProxyService);
  private readonly bankAccountService = inject(BankAccountProxyService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly translate = inject(TranslateService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  private readonly bankAccountMap = new Map<string, BankAccount>();

  readonly isLoading = signal(0);
  readonly saving = signal(false);
  /** True until a funding is persisted (creating a new one). */
  readonly isNew = signal(false);
  readonly funding = signal<CashFunding | null>(null);
  private readonly syncBreadcrumb = effect(() => this.breadcrumbService.setCurrent(this.isNew() ? 'Common_New' : this.funding()?.number), {
    allowSignalWrites: true,
  });

  // ---- Accounting / GL impact (#5024 #5032) ----
  /** The journal entry this funding booked when it posted. Null on a draft. */
  readonly journalEntryReference = computed(() => this.funding()?.journalEntryReference ?? null);
  readonly journalEntryDocumentNumber = computed(() => this.funding()?.journalEntryDocumentNumber ?? null);
  readonly showAccounting = computed(() => !!this.journalEntryReference());
  /** The component takes a list; a funding books exactly one entry. */
  readonly journalEntryReferences = computed(() => {
    const reference = this.journalEntryReference();
    return reference ? [reference] : [];
  });
  readonly request = signal<CashRequest | null>(null);
  readonly attachments = signal<S3Object[]>([]);
  private readonly allBankAccounts = signal<BankAccount[]>([]);
  /** Approved-and-unfunded requests to choose from (new mode only). */
  readonly fundableOptions = signal<SelectOption[]>([]);
  /** Live funding amount (drives the summary while editing/creating). */
  readonly amountValue = signal<number>(0);
  /** Live bank charges (incoming → deducted from what we net). */
  readonly bankingFeeValue = signal<number>(0);
  readonly securityFeeValue = signal<number>(0);
  /** Funding currency — from the selected request (new) or the loaded funding (edit/view). */
  readonly currency = signal<Currency>(Currency.USD);

  readonly isDraft = computed(() => this.funding()?.status === CashFundingStatus.Draft);
  /** The form is editable while creating or while the funding is still a Draft. */
  readonly isEditable = computed(() => this.isNew() || this.isDraft());

  readonly requestTotal = computed(() => this.request()?.totalAmount ?? 0);
  readonly underRequest = computed(() => Math.round((this.amountValue() - this.requestTotal()) * 100) / 100);
  /** Incoming: the fees are deducted from what actually lands in the bank. */
  readonly netCashReceived = computed(
    () => Math.round((this.amountValue() - this.bankingFeeValue() - this.securityFeeValue()) * 100) / 100,
  );
  /** Summary context rows: request total + under/over-request delta. */
  readonly summaryExtraRows = computed(() => [
    { label: this.t('CashFunding_RequestTotal', 'Request total'), value: this.requestTotal(), tone: 'muted' as const },
    {
      label: this.underRequest() < 0 ? this.t('CashFunding_Under', 'Under request') : this.t('CashFunding_Over', 'Over request'),
      value: this.underRequest(),
      tone: (this.underRequest() < 0 ? 'under' : 'over') as 'under' | 'over',
    },
  ]);

  /** Selectable banks — only those whose currency matches the funding's currency. */
  readonly bankAccountOptions = computed<SelectOption[]>(() =>
    this.allBankAccounts()
      .filter((acc) => acc.currency === this.currency())
      .map((acc) => ({ label: this.bankLabel(acc), value: acc.id! })),
  );

  readonly form: FormGroup<CashFundingForm> = this.formBuilder.group<CashFundingForm>({
    cashRequest: new FormControl<string | null>(null, [Validators.required]),
    fundingDate: new FormControl<Date | null>(new Date(), [Validators.required]),
    bankAccount: new FormControl<string | null>(null, [Validators.required]),
    amount: new FormControl<number | null>(0, [Validators.required, Validators.min(0.01)]),
    bankReference: new FormControl<string | null>(null),
    bankingFee: new FormControl<number | null>(0),
    securityFee: new FormControl<number | null>(0),
  });

  ngOnInit(): void {
    this.form.controls.amount.valueChanges.subscribe((v) => this.amountValue.set(v ?? 0));
    this.form.controls.bankingFee.valueChanges.subscribe((v) => this.bankingFeeValue.set(v ?? 0));
    this.form.controls.securityFee.valueChanges.subscribe((v) => this.securityFeeValue.set(v ?? 0));
    this.loadBankAccounts();

    const id = this.route.snapshot.queryParamMap.get('id');
    if (id) {
      this.isNew.set(false);
      this.load(id);
    } else {
      this.isNew.set(true);
      this.loadFundableRequests();
    }
  }

  private t(key: string, fallback: string): string {
    const v = this.translate.instant(key);
    return v === key ? fallback : v;
  }

  // ── Data loads ────────────────────────────────────────────────────────────
  private load(id: string): void {
    this.isLoading.update((v) => v + 1);
    this.service
      .getById(id)
      .pipe(finalize(() => this.isLoading.update((v) => v - 1)))
      .subscribe({
        next: (response) => {
          const data = response.data ?? null;
          if (!data) {
            this.funding.set(null);
            return;
          }
          this.applyFunding(data);
        },
        error: () => this.funding.set(null),
      });
  }

  private loadFundableRequests(): void {
    this.isLoading.update((v) => v + 1);
    this.service
      .getFundableRequests()
      .pipe(finalize(() => this.isLoading.update((v) => v - 1)))
      .subscribe({
        next: (response) => {
          this.fundableOptions.set(
            (response.data ?? []).map((r) => ({
              label: `${r.number} · ${this.dateLabel(r.weekStart)} – ${this.dateLabel(r.weekEnd)} · ${this.t('Approved', 'Approved')}`,
              value: r.id,
            })),
          );
        },
        error: () => this.fundableOptions.set([]),
      });
  }

  private dateLabel(value?: string): string {
    if (!value) return '';
    const d = new Date(value);
    return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
  }

  private loadRequest(cashRequestReference: string): void {
    this.isLoading.update((v) => v + 1);
    this.cashRequestService
      .getById(cashRequestReference)
      .pipe(finalize(() => this.isLoading.update((v) => v - 1)))
      .subscribe({
        next: (response) => this.request.set(response.data ?? null),
        error: () => this.request.set(null),
      });
  }

  private loadBankAccounts(): void {
    this.isLoading.update((v) => v + 1);
    this.bankAccountService
      .getAll()
      .pipe(finalize(() => this.isLoading.update((v) => v - 1)))
      .subscribe({
        next: (response) => {
          this.bankAccountMap.clear();
          const active = (response.data ?? []).filter((acc) => acc.id && acc.status === BankAccountStatus.ACTIVE);
          for (const acc of active) this.bankAccountMap.set(acc.id!, acc);
          this.allBankAccounts.set(active);
        },
        error: () => this.allBankAccounts.set([]),
      });
  }

  private bankLabel(acc: BankAccount): string {
    const cur = toCurrencyLabel(acc.currency);
    const last4 = (acc.bankAccountNumber ?? '').slice(-4);
    return [acc.bankName, cur, last4 ? `****${last4}` : ''].filter((s) => s).join(' · ');
  }

  // ── New mode: pick a Cash Request → load its breakdown + default the amount ───
  onCashRequestChange(id: string | null): void {
    this.request.set(null);
    if (!id) return;
    this.isLoading.update((v) => v + 1);
    this.cashRequestService
      .getById(id)
      .pipe(finalize(() => this.isLoading.update((v) => v - 1)))
      .subscribe({
        next: (response) => {
          const request = response.data ?? null;
          this.request.set(request);
          if (request) {
            this.currency.set(request.currency ?? Currency.USD);
            this.form.controls.amount.setValue(request.totalAmount ?? 0);
            // Currency changed → drop any bank selection that no longer matches.
            const bankId = this.form.controls.bankAccount.value;
            if (bankId && this.bankAccountMap.get(bankId)?.currency !== this.currency()) {
              this.form.controls.bankAccount.setValue(null);
            }
          }
        },
        error: () => this.request.set(null),
      });
  }

  // ── Display ─────────────────────────────────────────────────────────────────
  currencyLabel(currency: number | null | undefined): string {
    return toCurrencyLabel(currency);
  }

  statusLabel(status: number | undefined): string {
    return status == null ? '' : (CashFundingStatus[status] ?? '');
  }

  statusSeverity(status: number | undefined): 'success' | 'warn' | 'info' | 'danger' | 'secondary' {
    switch (status) {
      case CashFundingStatus.Posted:
        return 'success';
      case CashFundingStatus.Cancelled:
        return 'danger';
      default:
        return 'secondary';
    }
  }

  // ── Build payloads ────────────────────────────────────────────────────────────
  private buildInsert(): CashFundingInsert {
    const v = this.form.getRawValue();
    return {
      // Funder is derived server-side from the request's company; not sent.
      cashRequestReference: v.cashRequest ?? '',
      fundingDate: (v.fundingDate ?? new Date()).toISOString(),
      bankAccountReference: v.bankAccount ?? null,
      amount: v.amount ?? 0,
      currency: this.currency(),
      transferFee: v.bankingFee ?? 0,
      securityTax: v.securityFee ?? 0,
      bankingReference: v.bankReference ?? null,
    };
  }

  private buildUpdate(): CashFundingUpdate {
    const v = this.form.getRawValue();
    return {
      fundingDate: (v.fundingDate ?? new Date()).toISOString(),
      bankAccountReference: v.bankAccount ?? null,
      amount: v.amount ?? 0,
      transferFee: v.bankingFee ?? 0,
      securityTax: v.securityFee ?? 0,
      bankingReference: v.bankReference ?? null,
    };
  }

  private validate(): boolean {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      GlobalMessageUtil.warn(
        this.t('CashFunding', 'Cash Funding'),
        this.t('CashFunding_Invalid', 'Select a cash request, bank account and amount.'),
      );
      return false;
    }
    return true;
  }

  // ── Save (create or update the Draft) ─────────────────────────────────────────
  save(): void {
    if (!this.validate()) return;
    const id = this.funding()?.id;
    this.saving.set(true);
    this.isLoading.update((v) => v + 1);
    const request$ = id ? this.service.updateDraft(id, this.buildUpdate()) : this.service.createDraft(this.buildInsert());
    request$
      .pipe(
        finalize(() => {
          this.saving.set(false);
          this.isLoading.update((v) => v - 1);
        }),
      )
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            const wasNew = !id;
            this.applyFunding(response.data);
            if (wasNew && response.data.id) {
              this.router.navigate([], { queryParams: { id: response.data.id }, replaceUrl: true });
            }
            GlobalMessageUtil.success(this.t('CashFunding', 'Cash Funding'), this.t('CashFunding_DraftSaved', 'Draft saved.'));
          }
        },
      });
  }

  private applyFunding(funding: CashFunding): void {
    this.isNew.set(false);
    this.funding.set(funding);
    this.attachments.set(funding.attachments ?? []);
    this.amountValue.set(funding.amount ?? 0);
    this.currency.set(funding.currency ?? Currency.USD);
    this.patchForm(funding);
    if (funding.cashRequestReference && this.request()?.id !== funding.cashRequestReference) {
      this.loadRequest(funding.cashRequestReference);
    }
  }

  private patchForm(funding: CashFunding): void {
    this.form.patchValue({
      cashRequest: funding.cashRequestReference ?? null,
      fundingDate: funding.fundingDate ? new Date(funding.fundingDate) : new Date(),
      bankAccount: funding.bankAccountReference ?? null,
      amount: funding.amount ?? 0,
      bankReference: funding.bankingReference ?? null,
      bankingFee: funding.transferFee ?? 0,
      securityFee: funding.securityTax ?? 0,
    });
    if (funding.status !== CashFundingStatus.Draft) this.form.disable();
    else this.form.enable();
    // The Cash Request is fixed once a funding exists (only chosen while creating).
    this.form.controls.cashRequest.disable();
  }

  // ── Post a still-Draft funding (persisting the latest edits first) ──────────────
  post(): void {
    if (!this.validate()) return;
    this.confirmationService.confirm({
      header: this.t('CashFunding_Post_Header', 'Receive funding'),
      message: this.t(
        'CashFunding_Post_Message',
        'Posting records the funding in the bank and creates the journal entry (Dr bank / Cr intercompany). Continue?',
      ),
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-success',
      accept: () => this.doPost(),
    });
  }

  private doPost(): void {
    this.saving.set(true);
    this.isLoading.update((v) => v + 1);
    const bankingReference = this.form.controls.bankReference.value ?? null;
    const id = this.funding()?.id;

    if (id) {
      // Persist any pending edits, then post so the posted amounts/bank match the screen.
      this.service.updateDraft(id, this.buildUpdate()).subscribe({
        next: (saved) => {
          if (!saved.success || !saved.data) {
            this.stopPost();
            return;
          }
          this.postExisting(id, bankingReference);
        },
        error: () => this.stopPost(),
      });
      return;
    }

    // New funding: create the draft, then post it.
    this.service.createDraft(this.buildInsert()).subscribe({
      next: (created) => {
        if (!created.success || !created.data?.id) {
          this.stopPost();
          return;
        }
        this.funding.set(created.data);
        this.postExisting(created.data.id, bankingReference);
      },
      error: () => this.stopPost(),
    });
  }

  private postExisting(id: string, bankingReference: string | null): void {
    this.service
      .post(id, { bankTransactionNumber: bankingReference })
      .pipe(
        finalize(() => {
          this.saving.set(false);
          this.isLoading.update((v) => v - 1);
        }),
      )
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.applyFunding(response.data);
            if (!this.route.snapshot.queryParamMap.get('id')) {
              this.router.navigate([], { queryParams: { id }, replaceUrl: true });
            }
            GlobalMessageUtil.success(this.t('CashFunding', 'Cash Funding'), this.t('CashFunding_Posted', 'Funding received and posted.'));
          }
        },
      });
  }

  private stopPost(): void {
    this.saving.set(false);
    this.isLoading.update((v) => v - 1);
  }

  // ── Attachments (save the draft first; file comes from the shared bank-charges section) ───────
  onFileSelected(file: File): void {
    const id = this.funding()?.id;
    if (!id || !file) return;
    this.isLoading.update((v) => v + 1);
    this.service
      .uploadAttachment(id, file)
      .pipe(finalize(() => this.isLoading.update((v) => v - 1)))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.applyFunding(response.data);
            GlobalMessageUtil.success(this.t('CashFunding', 'Cash Funding'), this.t('CashFunding_Attached', 'Attachment uploaded.'));
          }
        },
      });
  }

  openAttachment(url: string | null | undefined): void {
    if (url) window.open(url, '_blank');
  }

  back(): void {
    this.router.navigate(['/cash-fundings']);
  }
}
